import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RMk, Skeletons } from '../components/kit';
import ListingAssistant from '../components/ListingAssistant';
import { viewAs } from '../api/client';
import NotifBell from '../components/NotifBell';
import useGreeting from '../hooks/useGreeting';
import usePerformanceScopes from '../hooks/usePerformanceScopes';
import { reducedMotion, useCountingRM } from '../hooks/useCountUp';
import { S } from '../lib/space';
import * as dashApi from '../api/dashboard';
import * as perfApi from '../api/performance';

const TONE = { warn: 'var(--warn)', bad: 'var(--bad)', accent: 'var(--accent)' };

// This calendar month as the inclusive { from, to } the performance API takes.
const thisMonth = () => {
  const n = new Date();
  const p = (x) => String(x).padStart(2, '0');
  const last = new Date(n.getFullYear(), n.getMonth() + 1, 0).getDate();
  return { from: `${n.getFullYear()}-${p(n.getMonth() + 1)}-01`, to: `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(last)}` };
};

// Shared line-art icon wrapper — the same 18px / 1.9-stroke set the More grid
// uses, so a module's mark is identical wherever an agent meets it.
const Icon = ({ children, join }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin={join ? 'round' : undefined}>
    {children}
  </svg>
);

// The six things an agent starts from home. Two ("Listing", "Booking form") is
// what this used to offer, against a ten-module app.
const SHORTCUTS = [
  { to: '/listings/new', label: 'Listing', icon: <Icon><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /></Icon> },
  { to: '/forms/new', label: 'Booking form', icon: <Icon><rect x="5" y="4" width="14" height="16" rx="2" /><path d="M9 15c1.5-2 2.5 1 4-1" /></Icon> },
  { to: '/sales/new', label: 'Sale', icon: <Icon><path d="M4 7h16M4 12h16M4 17h10" /></Icon> },
  { to: '/letters/new', label: 'Co-agency letter', icon: <Icon join><rect x="4" y="6" width="16" height="12" rx="2" /><polyline points="4 8 12 13 20 8" /></Icon> },
  // `badge` names a key in the summary's `badges` — co-share is the only module
  // where someone else is blocked on this agent, so it is the only one that earns
  // a count here rather than just a queue row.
  { to: '/co-share', label: 'Co-share', badge: 'coShare', icon: <Icon><circle cx="8" cy="9" r="3" /><circle cx="16" cy="9" r="3" /><path d="M3 19a5 5 0 0 1 10 0" /><path d="M13.5 15.5a5 5 0 0 1 7.5 3.5" /></Icon> },
  { to: '/tools', label: 'Tools', icon: <Icon><line x1="4" y1="8" x2="20" y2="8" /><circle cx="14" cy="8" r="2.6" fill="var(--chip)" /><line x1="4" y1="16" x2="20" y2="16" /><circle cx="8" cy="16" r="2.6" fill="var(--chip)" /></Icon> },
];

export default function HomePage() {
  const nav = useNavigate();
  const greeting = useGreeting();
  const [d, setD] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const perf = usePerformanceScopes();
  const [team, setTeam] = useState(null);

  useEffect(() => {
    dashApi.listHomeSummary().then((r) => setD(r?.data ?? r)).catch(() => {});
  }, []);

  // "Team this month" — the glance that makes the module get opened daily. Only
  // fetched once the scopes call confirms there is a team to summarise, and it
  // leads with whichever scope is widest: branch for a Centre Director, direct
  // recruits for everyone else.
  useEffect(() => {
    if (!perf.any) return undefined;
    let live = true;
    const scope = perf.can.branch ? 'branch' : 'direct';
    perfApi.getPerformanceSummary({ scope, ...thisMonth() })
      // Spreading `scope` in makes the object truthy WHATEVER came back, so the
      // card's own `team &&` guard could never be false once this resolved — a
      // thin payload rendered the card and threw on team.members. The card now
      // tests team?.members, and a response without one is treated as no team.
      .then((r) => { if (live) setTeam({ ...(r?.data ?? r), scope }); })
      .catch(() => { if (live) setTeam(null); });
    return () => { live = false; };
  }, [perf.any, perf.can.branch]);

  const gci = d?.gci || {};
  const tier = d?.tier || {};
  const money = d?.money || {};
  // Learn Mode's second door. Client-side and prepended rather than served from
  // /home/summary, because "have you finished the practice deal" is a fact about
  // this device, not about the agent — and the server has no table for it.
  // Drops off the queue for good once the spine lesson is finished.
  const learnDone = (() => {
    try { return !!JSON.parse(localStorage.getItem('cid.learn.progress') || '{}')?.spine?.done; } catch { return false; }
  })();
  const queue = [
    ...(learnDone ? [] : [{ kind: 'learn', tone: 'accent', text: 'Learn the app in one sitting', sub: 'Step-by-Step Guide to CIDAS', to: '/learn', cta: 'Start' }]),
    ...(d?.needsYou || []),
  ];
  const preview = d?.needsYouPreview ?? 3;
  const shown = queueOpen ? queue : queue.slice(0, preview);
  const hidden = queue.length - shown.length;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div style={{ background: '#fff', borderRadius: 999, padding: '4px 12px' }}>
          <img src="/cid_fulllogo.png" alt="CID" style={{ height: 22 }} />
        </div>
        <div style={{ flex: 1 }} />
        <NotifBell />
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: S.page, display: 'flex', flexDirection: 'column', gap: S.block }}>
        {!d ? (
          <Skeletons n={4} height={86} />
        ) : (
          <>
            {/* Beat 1 — who you are, and how the year is going. */}
            <div className="rise-in">
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{greeting},</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginTop: 2 }}>
                <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-.01em' }}>{d.agent?.full_name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{'REN ' + String(d.agent?.ren_no || '').replace(/^REN\s*/i, '')}</div>
              </div>
            </div>

            <div className="rise-in">
              <Hero gci={gci} tier={tier} />
            </div>

            {/* Beat 2 — the figures. Money as three buckets that don't overlap:
                what HQ hasn't freed yet, what's freed and waiting on a payout
                run, and what has actually left. The Earnings chip vocabulary. */}
            <div className="rise-in d1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: S.grid }}>
              <Tile label="LOCKED" value={RMk(money.locked)} color="var(--warn)" onClick={() => nav('/earnings')} />
              <Tile label="RELEASED" value={RMk(money.released)} color="var(--accent)" onClick={() => nav('/earnings')} />
              <Tile
                label={money.hasLedger ? 'PAID YTD' : 'PAID'}
                value={RMk(money.hasLedger ? money.paidThisYear : money.paid)}
                color="var(--ok)"
                onClick={() => nav('/earnings')}
              />
            </div>

            <div className="rise-in d1" style={{ display: 'flex', gap: S.grid }}>
              <Row label="Closed this month" value={`${d.closingsThisMonth ?? 0}`} />
              {/* Rank counts the agents strictly ahead, so an agent with nothing
                  booked this year is "ahead of nobody" and lands at #1. The
                  server says whether the position was earned; unearned, it is
                  not shown. */}
              <Row
                label="Leaderboard"
                value={d.leaderboard?.ranked ? `#${d.leaderboard.rank} of ${d.leaderboard.of}` : 'Not ranked yet'}
                valueColor={d.leaderboard?.ranked ? 'var(--primary)' : 'var(--muted)'}
              />
            </div>

            {/* Beat 3 — everything across the app that is waiting on this agent,
                above the shortcuts rather than below three rows of reference
                numbers. */}
            <div className="rise-in d2" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <SectionLabel style={{ marginTop: 0 }}>NEEDS YOU</SectionLabel>
                {queue.length > 0 && (
                  <span style={{ minWidth: 19, height: 19, padding: '0 6px', borderRadius: 999, background: 'var(--bad)', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {queue.length}
                  </span>
                )}
              </div>

              {queue.length === 0 ? (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, padding: '22px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ok)' }}>You&rsquo;re all clear</div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>Nothing is waiting on you right now.</div>
                </div>
              ) : (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, overflow: 'hidden' }}>
                  {shown.map((a, i) => (
                    <QueueRow
                      key={`${a.kind}-${a.to || i}`}
                      item={a}
                      last={i === shown.length - 1 && hidden === 0}
                      onOpen={() => a.to && nav(a.to)}
                    />
                  ))}
                  {hidden > 0 && (
                    <div
                      className="tap"
                      onClick={() => setQueueOpen(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setQueueOpen(true); } }}
                      style={{ padding: S.rowPad, borderTop: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer' }}
                    >
                      Show {hidden} more
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Beat 4 — shortcuts and everything below. */}
            <div className="rise-in d3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: S.grid }}>
              {SHORTCUTS.map((s) => {
                // Same count, and the same treatment, as the More tab's badge —
                // an agent who learns the dot on one screen reads it on the other.
                const count = s.badge ? Number(d.badges?.[s.badge]) || 0 : 0;
                return (
                  <div
                    key={s.to}
                    className="tap"
                    onClick={() => nav(s.to)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); nav(s.to); } }}
                    style={{ position: 'relative', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, padding: '16px 8px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9, cursor: 'pointer', textAlign: 'center' }}
                  >
                    <span style={{ color: 'var(--primary)', display: 'flex' }}>{s.icon}</span>
                    {/* Two lines are reserved whether or not the label needs them:
                        only "Co-agency letter" wraps, and without this its row
                        stands taller than the row above it for one word. */}
                    <span style={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.2, minHeight: '2.4em', display: 'flex', alignItems: 'center' }}>{s.label}</span>
                    {count > 0 && (
                      <span
                        aria-label={`${count} waiting`}
                        style={{ position: 'absolute', top: 8, right: 8, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'var(--bad)', color: '#fff', fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                      >
                        {count > 9 ? '9+' : count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* team this month — leaders only */}
            {team?.members && (
              <div
                className="rise-in d3 tap"
                onClick={() => nav('/team/performance')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); nav('/team/performance'); } }}
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, padding: S.cardPad, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.05em' }}>
                    {team.scope === 'branch' ? 'BRANCH THIS MONTH' : 'TEAM THIS MONTH'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 5 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ok)' }}>{RMk(team.gci)}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                      {team.deals} deal{team.deals === 1 ? '' : 's'} · {team.members.producing}/{team.members.total} producing
                    </span>
                  </div>
                  {team.members.dormant > 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--warn)', marginTop: 4, fontWeight: 500 }}>
                      {team.members.dormant} with no closing in 90 days
                    </div>
                  )}
                </div>
                <span style={{ color: 'var(--muted)', flex: 'none' }}>›</span>
              </div>
            )}

            {/* recent — the notification feed itself, so this and the bell can
                never disagree about the same week */}
            {d.recent?.length > 0 && (
              <div className="rise-in d3" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <SectionLabel style={{ marginTop: 0 }}>RECENT ACTIVITY</SectionLabel>
                  <a onClick={() => nav('/notifs')} style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>See all</a>
                </div>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, overflow: 'hidden' }}>
                  {d.recent.map((n, i) => (
                    <div key={n.id || i} className={n.to ? 'tap' : undefined} onClick={() => n.to && nav(n.to)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: S.rowPad, borderBottom: i < d.recent.length - 1 ? '1px solid var(--border)' : 'none', cursor: n.to ? 'pointer' : 'default' }}>
                      {n.unread && <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--primary)', flex: 'none' }} />}
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div style={{ fontSize: 13, fontWeight: n.unread ? 600 : 500 }}>{n.t}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{n.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Clears the assistant button. Without it the last card can never
                be scrolled out from under the FAB, so whatever lands there is
                permanently half-covered. */}
            <div style={{ height: 56 }} />
          </>
        )}
      </div>

      {/* CID Assistant — ask about your sales, listings, earnings, team; voice or text.
          Not while HQ views as the agent: it greets the agent by name and then
          refuses every question in a read-only view. */}
      {!viewAs.active && <div onClick={() => setAiOpen(true)} style={{ position: 'absolute', right: 18, bottom: 18, width: 54, height: 54, borderRadius: 50, background: 'var(--navy, #241854)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,.25)', zIndex: 5 }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.9 5.7a2 2 0 0 0 1.27 1.27L21 11l-5.83 1.9a2 2 0 0 0-1.27 1.27L12 20l-1.9-5.83a2 2 0 0 0-1.27-1.27L3 11l5.83-2.03A2 2 0 0 0 10.1 7.7L12 2z" /><circle cx="19" cy="5" r="1.6" /><circle cx="5" cy="19" r="1.3" /></svg>
      </div>}

      {aiOpen && <ListingAssistant onClose={() => setAiOpen(false)} />}
    </div>
  );
}

/**
 * The hero.
 *
 * The ring is now a real progress meter: it fills on how far through the CURRENT
 * tier band the agent is. It used to fill on the tier number itself — a 70% tier
 * drew 70% of a ring — which looked like progress and measured nothing.
 *
 * The headline is GCI COLLECTED this year — commission the office has actually
 * received and knocked off against an invoice. That is the same figure the
 * Earnings tab, the leaderboard below and the admin portal all report, and the
 * same one the tier engine measures a step-up on.
 *
 * It used to be this month's GCI, derived here from the agent's closed sale
 * lines. That answered a different question ("how much have you sold?") under
 * the same label, and the two drifted RM 1.75m apart across 92 agents — an agent
 * could read RM 139k here and RM 700 one tab away. There is no monthly
 * equivalent of the collected figure to fall back on: nothing in the schema
 * records WHEN commission was collected per month, so the month windows go
 * rather than get re-sourced. "Closed this month" below the card keeps a live
 * sense of the month, as a DEAL COUNT, which claims nothing about cash.
 */
function Hero({ gci, tier }) {
  const pct = Math.max(0, Math.min(100, Number(tier.progressPct) || 0));
  // `collected` is the field this build wants; `ytd` is what an agent server
  // that has not shipped yet still sends. Both carry the same meaning once the
  // server is deployed, so the fallback is a transition aid, not a second basis.
  const collected = Number(gci.collected ?? gci.ytd) || 0;

  // The distance still to climb, in three states rather than two.
  //
  // `basis` decides which clock the distance is on: below 85% the ladder is
  // climbed on lifetime GCI, and at 85% and above the step-ups are earned on GCI
  // booked inside this calendar year. Without saying which, "RM 114k to go"
  // reads as the same kind of number in both halves of the ladder.
  //
  // The third state is a band already cleared — an agent can pass the threshold
  // before HQ recomputes their tier. "RM 0 more → 87% tier" is arithmetically
  // true and reads as a bug, so a cleared band says it has been cleared.
  const remaining = !tier.next
    ? 'Top tier — nothing above 90%'
    : tier.toNext <= 0
      ? `${tier.next}% tier reached — awaiting HQ review`
      : `${RMk(tier.toNext)} more ${tier.basis === 'in-year' ? 'this year' : 'lifetime GCI'} → ${tier.next}% tier`;

  // The migrated tier outran the migrated history: this agent is paid at a rate
  // their recorded GCI has not reached, so the meter is measuring from zero
  // rather than from the band they are nominally standing in. Say so — a meter
  // whose scale silently changed is worse than one that explains itself.
  const scaleNote = tier.belowBand && tier.next
    ? `Measured from RM 0 — your recorded GCI is below the ${tier.current}% threshold`
    : null;

  const countingGci = useCountingRM(collected);

  return (
    <div style={{ background: 'linear-gradient(135deg,#241854,#18345E)', borderRadius: 20, padding: 22, color: '#fff', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
        <TierRing pct={pct} tier={tier} />

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div style={{ fontSize: 11.5, opacity: 0.72, letterSpacing: '.07em', fontWeight: 600 }}>GCI COLLECTED THIS YEAR</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
            {/* Tabular figures so the sweep doesn't jitter the digits' width. */}
            <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {countingGci}
            </span>
          </div>
          {/* Says what the headline counts. Without it "RM 700" beside "RM 2.1M
              lifetime" reads as a broken screen rather than as money that has
              closed but not yet been collected. */}
          <div style={{ fontSize: 11.5, opacity: 0.75 }}>
            Received &amp; knocked off · {RMk(gci.lifetime)} lifetime
          </div>
        </div>
      </div>

      {/* The ring's caption, and the longest string in the card — "87% tier
          reached — awaiting HQ review" plus the below-band note both run past
          what is left beside a 100px ring. Given the card's full width it sets
          on one line instead of wrapping a single orphaned word. */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,.13)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ fontSize: 12.5, opacity: 0.9, lineHeight: 1.35 }}>{remaining}</div>
        {scaleNote && <div style={{ fontSize: 11.5, opacity: 0.62, lineHeight: 1.35 }}>{scaleNote}</div>}
      </div>
    </div>
  );
}

/**
 * The tier meter, as a stroked SVG arc rather than a conic gradient.
 *
 * A conic-gradient can only be animated behind `@property`, which Safari did not
 * ship until 16.4 — so on the phones this app is actually installed on it would
 * snap. A dash-offset transition sweeps everywhere, and the round cap gives the
 * arc an end the gradient never had.
 *
 * It sweeps from empty on mount: the fill IS the message, and a meter that is
 * simply already full when you look at it doesn't communicate that it moved.
 */
function TierRing({ pct, tier }) {
  const SIZE = 100;
  const STROKE = 9;
  const r = (SIZE - STROKE) / 2;
  const circumference = 2 * Math.PI * r;

  // Start empty, then hand the real value over one frame later so the transition
  // has two states to travel between. Reduced motion goes straight to the value.
  const [drawn, setDrawn] = useState(() => (reducedMotion() ? pct : 0));
  useEffect(() => {
    if (reducedMotion()) { setDrawn(pct); return undefined; }
    const raf = requestAnimationFrame(() => setDrawn(pct));
    return () => cancelAnimationFrame(raf);
  }, [pct]);

  return (
    <div
      style={{ width: SIZE, height: SIZE, flex: 'none', position: 'relative' }}
      role="img"
      aria-label={tier.next ? `${tier.current}% tier, ${Math.round(pct)}% of the way to ${tier.next}%` : `${tier.current}% tier — the top of the ladder`}
    >
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={SIZE / 2} cy={SIZE / 2} r={r} fill="none" stroke="rgba(255,255,255,.16)" strokeWidth={STROKE} />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={r}
          fill="none"
          stroke="#5AA1EA"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={circumference}
          // Offset goes in `style`, not as an attribute. A presentation
          // attribute and a CSS property are different inputs to the cascade,
          // and only the CSS one is reliably transitioned — set as an attribute
          // this jumps to its final value on the first frame in some engines.
          style={{
            strokeDashoffset: circumference * (1 - Math.max(0, Math.min(100, drawn)) / 100),
            transition: 'stroke-dashoffset 1s cubic-bezier(.22,.61,.36,1)',
          }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{tier.current}%</div>
        <div style={{ fontSize: 10.5, opacity: 0.7, letterSpacing: '.07em', marginTop: 3 }}>TIER</div>
      </div>
    </div>
  );
}

// One action row — every row is a link to the place that clears it.
function QueueRow({ item, last, onOpen }) {
  const tone = TONE[item.tone] || 'var(--accent)';
  return (
    <div
      className={item.to ? 'tap' : undefined}
      onClick={onOpen}
      role={item.to ? 'button' : undefined}
      tabIndex={item.to ? 0 : undefined}
      onKeyDown={(e) => { if (item.to && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); } }}
      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: S.rowPad, borderBottom: last ? 'none' : '1px solid var(--border)', cursor: item.to ? 'pointer' : 'default' }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 50, background: tone, flex: 'none' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{item.text}</div>
        {item.sub && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sub}</div>}
      </div>
      {item.cta
        ? <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--primary)', flex: 'none' }}>{item.cta}</span>
        : <span style={{ color: 'var(--muted)', flex: 'none' }}>›</span>}
    </div>
  );
}

const SectionLabel = ({ children, style }) => <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginTop: 4, ...style }}>{children}</div>;
const Tile = ({ label, value, color, onClick }) => (
  <div
    className={onClick ? 'tap' : undefined}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
    style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, padding: S.tilePad, cursor: onClick ? 'pointer' : 'default' }}
  >
    <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.05em' }}>{label}</div>
    <div style={{ fontSize: 17, fontWeight: 700, marginTop: 6, color: color || 'var(--text)', letterSpacing: '-.01em' }}>{value}</div>
  </div>
);
const Row = ({ label, value, valueColor }) => (
  <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, padding: S.rowPad, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
    <span style={{ fontSize: 13, color: 'var(--muted)' }}>{label}</span>
    <span style={{ fontSize: 13.5, fontWeight: 600, color: valueColor || 'var(--text)', whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);
